import { faEye, faFilter, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, Modal, Spinner, Table } from "react-bootstrap";
import SwaggerContext from "../../SwaggerContext";
import { notifyError, notifySuccess } from "../../actions/notifications";
import ConfirmationModal from "../../components/ConfirmationModal";
import PaginationComponent from "../../components/pagination";
import { createApiCallback, logApiError } from "../../utils/error-handler";

const initialState = {
  pagination: {
    page: 0,
    limit: 10,
  },
};

const loadErrorLogs = (errorLogApi, setData, opts) => {
  errorLogApi.getErrorLogs(opts, createApiCallback(
    (data) => {
      setData(data);
    },
    (errorMessage, errorInfo, error, response) => {
      logApiError("Load Error Logs", error, response);
      notifyError(`Ha habido un error al cargar la lista de errores: ${errorMessage}`);
    }
  ));
};

const removeErrorLog = (errorLogApi, setData, id, pageNumber) => {
  errorLogApi.deleteErrorLogById(id, createApiCallback(
    () => {
      notifySuccess("Error eliminado correctamente.");
      let opts = initialState.pagination;
      opts.page = pageNumber;
      loadErrorLogs(errorLogApi, setData, opts);
    },
    (errorMessage, errorInfo, error, response) => {
      logApiError("Delete Error Log", error, response);
      notifyError(`Ha habido un error al borrar el error: ${errorMessage}`);
    },
    204
  ));
};

const ErrorLogTable = React.memo(
  ({ errorLogApi, errorLogs, setData, pageNumber, filter, setFilter }) => {
    const errorHeaders = [
      { type: "userId", name: "Usuario ID" },
      { type: "errorType", name: "Tipo" },
      { type: "errorMessage", name: "Mensaje" },
      { type: "errorSource", name: "Origen" },
      { type: "createdAt", name: "Fecha" },
    ];
    const [deleteArguments, setDeleteArguments] = useState();
    const [showModal, setShowModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filterType, setFilterType] = useState("");
    const [filterValue, setFilterValue] = useState("");
    const [selectedError, setSelectedError] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleString("es-ES");
    };

    const getErrorSourceBadge = (source) => {
      const colors = {
        frontend: "primary",
        backend: "danger",
      };
      return colors[source] || "secondary";
    };

    const applyFilter = () => {
      setShowFilterModal(false);
      if (filterType) {
        setFilter((prevFilter) => ({
          ...prevFilter,
          [filterType]: filterValue === "" ? null : filterValue,
        }));
      }
    };

    const clearFilters = () => {
      setShowFilterModal(false);
      setFilter({
        userId: null,
        errorType: null,
        errorMessage: null,
        errorSource: null,
        createdAt: null,
      });
      setFilterValue("");
    };

    const handleViewDetails = (errorLog) => {
      setSelectedError(errorLog);
      setShowDetailsModal(true);
    };

    const renderFilterControl = () => {
      if (filterType === "errorSource") {
        return (
          <select
            className="form-select"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
          >
            <option value="">Selecciona origen</option>
            <option value="frontend">Frontend</option>
            <option value="backend">Backend</option>
          </select>
        );
      }

      if (filterType === "createdAt") {
        return (
          <input
            type="datetime-local"
            className="form-control"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
          />
        );
      }

      return (
        <input
          type="text"
          className="form-control"
          placeholder="Filtro"
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
        />
      );
    };

    useEffect(() => {
      if (showFilterModal && filterType && filter[filterType] !== undefined) {
        setFilterValue(filter[filterType] ?? "");
      }
    }, [showFilterModal, filterType, filter]);

    return (
      <>
        <Table striped bordered hover variant="dark mt-1 text-center">
          <thead>
            <tr>
              <th>#</th>
              {errorHeaders.map((header, i) => {
                return (
                  <th key={i}>
                    {header.name}{" "}
                    <FontAwesomeIcon
                      style={{ cursor: "pointer" }}
                      icon={faFilter}
                      onClick={() => {
                        setFilterType(header.type);
                        setShowFilterModal(true);
                      }}
                    />
                  </th>
                );
              })}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {errorLogs.map((errorLog, i) => {
              return (
                <tr key={i} style={{ cursor: "pointer" }}>
                  <td>{i + 1}</td>
                  <td>{errorLog.userId || "N/A"}</td>
                  <td>
                    <span className="badge bg-warning text-dark">
                      {errorLog.errorType}
                    </span>
                  </td>
                  <td
                    style={{
                      maxWidth: "300px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={errorLog.errorMessage}
                  >
                    {errorLog.errorMessage}
                  </td>
                  <td>
                    <span
                      className={`badge bg-${getErrorSourceBadge(
                        errorLog.errorSource
                      )}`}
                    >
                      {errorLog.errorSource}
                    </span>
                  </td>
                  <td>{formatDate(errorLog.createdAt)}</td>
                  <td>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(errorLog);
                      }}
                      className="me-1"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowModal(true);
                        setDeleteArguments([
                          errorLogApi,
                          setData,
                          errorLog.id,
                          pageNumber,
                        ]);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <ConfirmationModal
          show={showModal}
          setShowModal={setShowModal}
          deleteFunction={removeErrorLog}
          deleteArguments={deleteArguments}
        />

        {/* Modal de filtros */}
        <Modal
          show={showFilterModal}
          onHide={() => setShowFilterModal(false)}
          dialogClassName="modal-50w"
        >
          <Modal.Header closeButton>
            <Modal.Title>Filtrar por {filterType}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {renderFilterControl()}
            <div className="mt-3">
              <strong>Filtros activos:</strong>
              <ul>
                <li>Usuario ID: {filter.userId || "Ninguno"}</li>
                <li>Tipo: {filter.errorType || "Ninguno"}</li>
                <li>Mensaje: {filter.errorMessage || "Ninguno"}</li>
                <li>Origen: {filter.errorSource || "Ninguno"}</li>
                <li>
                  Fecha: {filter.createdAt ? formatDate(filter.createdAt) : "Ninguno"}
                </li>
              </ul>
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <Button className="mx-2" variant="success" onClick={applyFilter}>
              Aceptar
            </Button>
            <Button variant="warning" onClick={clearFilters}>
              Limpiar filtros
            </Button>
            <Button variant="danger" onClick={() => setShowFilterModal(false)}>
              Cancelar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de detalles */}
        <Modal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Detalles del Error #{selectedError?.id}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedError && (
              <>
                <div className="mb-3">
                  <strong>Usuario ID:</strong> {selectedError.userId || "N/A"}
                </div>
                <div className="mb-3">
                  <strong>Tipo:</strong>{" "}
                  <span className="badge bg-warning text-dark">
                    {selectedError.errorType}
                  </span>
                </div>
                <div className="mb-3">
                  <strong>Origen:</strong>{" "}
                  <span
                    className={`badge bg-${getErrorSourceBadge(
                      selectedError.errorSource
                    )}`}
                  >
                    {selectedError.errorSource}
                  </span>
                </div>
                <div className="mb-3">
                  <strong>Mensaje:</strong>
                  <p className="mt-1 p-2 bg-light border rounded text-dark">
                    {selectedError.errorMessage}
                  </p>
                </div>
                {selectedError.stackTrace && (
                  <div className="mb-3">
                    <strong>Stack Trace:</strong>
                    <pre
                      className="mt-1 p-2 bg-dark text-light border rounded"
                      style={{
                        maxHeight: "200px",
                        overflow: "auto",
                        fontSize: "0.85rem",
                      }}
                    >
                      {selectedError.stackTrace}
                    </pre>
                  </div>
                )}
                {selectedError.httpMethod && (
                  <div className="mb-3">
                    <strong>Método HTTP:</strong> {selectedError.httpMethod}
                  </div>
                )}
                {selectedError.requestUrl && (
                  <div className="mb-3">
                    <strong>URL:</strong>
                    <p className="mt-1 p-2 bg-light border rounded small text-dark">
                      {selectedError.requestUrl}
                    </p>
                  </div>
                )}
                {selectedError.httpStatusCode && (
                  <div className="mb-3">
                    <strong>Código HTTP:</strong> {selectedError.httpStatusCode}
                  </div>
                )}
                {selectedError.userAgent && (
                  <div className="mb-3">
                    <strong>User Agent:</strong>
                    <p className="mt-1 p-2 bg-light border rounded small text-dark">
                      {selectedError.userAgent}
                    </p>
                  </div>
                )}
                {selectedError.ipAddress && (
                  <div className="mb-3">
                    <strong>IP:</strong> {selectedError.ipAddress}
                  </div>
                )}
                {selectedError.additionalInfo && (
                  <div className="mb-3">
                    <strong>Información Adicional:</strong>
                    <pre
                      className="mt-1 p-2 bg-light border rounded text-dark"
                      style={{ maxHeight: "150px", overflow: "auto" }}
                    >
                      {JSON.stringify(
                        JSON.parse(selectedError.additionalInfo),
                        null,
                        2
                      )}
                    </pre>
                  </div>
                )}
                <div className="mb-3">
                  <strong>Fecha:</strong> {formatDate(selectedError.createdAt)}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
              Cerrar
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setShowDetailsModal(false);
                setShowModal(true);
                setDeleteArguments([
                  errorLogApi,
                  setData,
                  selectedError.id,
                  pageNumber,
                ]);
              }}
            >
              Eliminar
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
);

ErrorLogTable.displayName = "ErrorLogTable";
ErrorLogTable.propTypes = {
  errorLogs: PropTypes.array,
  setData: PropTypes.func,
  errorLogApi: PropTypes.object,
  pageNumber: PropTypes.number,
  filter: PropTypes.object,
  setFilter: PropTypes.func,
};

const List = () => {
  const { errorLogApi } = useContext(SwaggerContext);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState({
    userId: null,
    errorType: null,
    errorMessage: null,
    errorSource: null,
    createdAt: null,
  });
  let [opts, setOpts] = useState(initialState.pagination);
  
  useEffect(() => {
    loadErrorLogs(errorLogApi, setData, { ...opts, ...filter });
  }, [opts, filter, errorLogApi]);

  if (null === data) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "calc(100vh - 200px)" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Fragment>
      <h2 className="mb-3">Registro de Errores del Sistema</h2>
      <div className="mb-3">
        <strong>Total de errores:</strong> {data.totalElements || 0}
      </div>
      <ErrorLogTable
        errorLogs={data.errorLogs}
        setData={setData}
        errorLogApi={errorLogApi}
        pageNumber={data.page}
        filter={filter}
        setFilter={setFilter}
      />
      <div className="d-flex justify-content-center align-items-center">
        <PaginationComponent pages={data.totalPages} setOpts={setOpts} currentPage={data.page} />
      </div>
    </Fragment>
  );
};

export default List;
